import React, { useState, useEffect, useRef } from 'react';
import { Calendar, Clock, RefreshCw, LogIn, LogOut, ChevronLeft, ChevronRight, AlertCircle, Plus, X, Loader } from 'lucide-react';
import { format, addDays, subDays } from 'date-fns';

const START_HOUR = 6;
const END_HOUR   = 21;
const HOUR_HEIGHT_PX = 64; // slightly taller for full-page view

const pad = (n) => String(n).padStart(2, '0');

const toMinutes = (dateStr) => {
  const d = new Date(dateStr);
  return d.getHours() * 60 + d.getMinutes();
};

const formatTime = (dateStr) => {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  const h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${pad(m)} ${ampm}`;
};

const isoDateStr = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

// Build a local Date from 'yyyy-MM-dd' — avoids the UTC midnight trap
const localDateFromStr = (str) => {
  const [y, m, d] = str.split('-').map(Number);
  return new Date(y, m - 1, d);
};

const EVENT_COLORS = [
  '#4f86f7', '#e06c75', '#56b6c2', '#98c379',
  '#e5c07b', '#c678dd', '#61afef', '#d19a66',
];
const colorForCalendar = (calId) => {
  let hash = 0;
  for (let i = 0; i < (calId || '').length; i++) hash = (hash * 31 + calId.charCodeAt(i)) & 0xffff;
  return EVENT_COLORS[hash % EVENT_COLORS.length];
};

export default function CalendarSync({
  user,
  gapiReady,
  googleSignedIn,
  onGoogleSignIn,
  onGoogleSignOut,
  onCreateEvent,
}) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [events, setEvents]           = useState([]);
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState('');
  const [expanded, setExpanded]       = useState(null);
  const nowLineRef                    = useRef(null);

  // ─── Add Event form state ─────────────────────────────────────────────────
  const [showAddForm, setShowAddForm]   = useState(false);
  const [newTitle, setNewTitle]         = useState('');
  const [newDate, setNewDate]           = useState(isoDateStr(new Date()));
  const [newStartTime, setNewStartTime] = useState('09:00');
  const [newEndTime, setNewEndTime]     = useState('10:00');
  const [newAllDay, setNewAllDay]       = useState(false);
  const [savingEvent, setSavingEvent]   = useState(false);
  const [addError, setAddError]         = useState('');

  useEffect(() => {
    if (googleSignedIn && gapiReady) fetchEvents();
    if (!googleSignedIn) setEvents([]);
  }, [googleSignedIn, gapiReady, currentDate]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (nowLineRef.current) {
      nowLineRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [events]);

  const fetchEvents = async () => {
    setLoading(true);
    setError('');
    try {
      const dateStr = isoDateStr(currentDate);
      const timeMin = new Date(`${dateStr}T00:00:00`).toISOString();
      const timeMax = new Date(`${dateStr}T23:59:59`).toISOString();

      const response = await window.gapi.client.calendar.events.list({
        calendarId: 'primary',
        timeMin,
        timeMax,
        singleEvents: true,
        orderBy: 'startTime',
        maxResults: 50,
      });

      const items = response.result.items || [];
      setEvents(items.filter(e => e.start?.dateTime));
    } catch (e) {
      console.error('[OPP Calendar] fetch error:', e);
      setError('Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  };

  // ─── Add Event handlers ───────────────────────────────────────────────────
  const openAddForm = () => {
    setNewTitle('');
    setNewDate(isoDateStr(currentDate));
    setNewStartTime('09:00');
    setNewEndTime('10:00');
    setNewAllDay(false);
    setAddError('');
    setShowAddForm(true);
  };

  const handleAddEvent = async () => {
    if (!newTitle.trim()) {
      setAddError('Please enter a title for the event.');
      return;
    }
    setSavingEvent(true);
    setAddError('');
    try {
      if (newAllDay) {
        await onCreateEvent({
          title:  newTitle.trim(),
          allDay: true,
          date:   newDate,
        });
      } else {
        const [y, m, d]   = newDate.split('-').map(Number);
        const [sh, sm]    = newStartTime.split(':').map(Number);
        const [eh, em]    = newEndTime.split(':').map(Number);
        const start = new Date(y, m - 1, d, sh, sm);
        const end   = new Date(y, m - 1, d, eh, em);
        if (end <= start) {
          setAddError('End time must be after start time.');
          setSavingEvent(false);
          return;
        }
        await onCreateEvent({ title: newTitle.trim(), start, end });
      }
      setShowAddForm(false);
      // Jump the view to the event's date and refresh
      setCurrentDate(localDateFromStr(newDate));
      fetchEvents();
    } catch (e) {
      console.error('[OPP Calendar] create event error:', e);
      setAddError('Could not create the event. Please try again.');
    } finally {
      setSavingEvent(false);
    }
  };

  const positionEvent = (event) => {
    const startMin     = toMinutes(event.start.dateTime);
    const endMin       = toMinutes(event.end.dateTime);
    const clampedStart = Math.max(startMin, START_HOUR * 60);
    const clampedEnd   = Math.min(endMin,   END_HOUR * 60);
    const top    = ((clampedStart - START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
    const height = Math.max(((clampedEnd - clampedStart) / 60) * HOUR_HEIGHT_PX, 24);
    return { top, height };
  };

  const getNowPosition = () => {
    if (isoDateStr(new Date()) !== isoDateStr(currentDate)) return null;
    const minutes = new Date().getHours() * 60 + new Date().getMinutes();
    if (minutes < START_HOUR * 60 || minutes > END_HOUR * 60) return null;
    return ((minutes - START_HOUR * 60) / 60) * HOUR_HEIGHT_PX;
  };

  const nowPosition = getNowPosition();
  const totalHeight = (END_HOUR - START_HOUR) * HOUR_HEIGHT_PX;
  const isToday = isoDateStr(currentDate) === isoDateStr(new Date());

  // ── Not connected screen ──────────────────────────────────────────────────
  if (!googleSignedIn) {
    return (
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl border border-slate-200 p-8 shadow-sm">
          <div className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-primary-light)' }}>
              <Calendar size={32} style={{ color: 'var(--color-primary)' }} />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl font-bold text-slate-800 mb-2">Google Calendar</h1>
              <p className="text-slate-600">Connect your Google Calendar to see your schedule here and alongside your tasks.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <h3 className="font-semibold text-slate-800 mb-2">Connection Status</h3>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-slate-300"></div>
                <p className="text-slate-700">Not connected</p>
              </div>
            </div>

            <div className="rounded-lg p-6" style={{ backgroundColor: 'var(--color-primary-light)', border: '1px solid var(--color-primary)' }}>
              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                  <AlertCircle size={18} className="text-red-600 flex-shrink-0 mt-0.5" />
                  <p className="text-red-700 text-sm">{error}</p>
                </div>
              )}
              <button
                onClick={onGoogleSignIn}
                disabled={!gapiReady}
                className="w-full px-6 py-3 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <LogIn size={20} />
                {gapiReady ? 'Connect Google Calendar' : 'Loading...'}
              </button>
            </div>

            <div className="p-4 bg-slate-50 border border-slate-200 rounded-lg">
              <h4 className="font-semibold text-slate-800 mb-2">Privacy & Security</h4>
              <p className="text-sm text-slate-700">
                Access is limited to viewing and adding calendar events. We never store your Google credentials. Revoke access anytime from your Google Account settings.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Connected: full calendar view ─────────────────────────────────────────
  return (
    <div>
      {/* Header card */}
      <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Calendar</h1>
            <div className="mt-2">
              <p className="text-xs text-slate-500 uppercase tracking-wide">
                {isToday ? 'Today' : 'Viewing'}
              </p>
              <p className="text-sm font-semibold text-slate-700">
                {format(currentDate, 'EEEE, MMMM d, yyyy')}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Date navigation */}
            <div className="flex items-center gap-2 border border-slate-200 rounded-lg p-2 bg-slate-50">
              <button
                onClick={() => setCurrentDate(d => subDays(d, 1))}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <ChevronLeft size={20} className="text-slate-600" />
              </button>
              <div className="flex flex-col gap-2">
                <input
                  type="date"
                  value={format(currentDate, 'yyyy-MM-dd')}
                  onChange={(e) => setCurrentDate(localDateFromStr(e.target.value))}
                  className="px-3 py-1 text-sm border border-slate-300 rounded-lg focus:outline-none cursor-pointer"
                />
                <button
                  onClick={() => setCurrentDate(new Date())}
                  className="px-3 py-1 text-sm font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: 'var(--color-primary)' }}
                >
                  Go to Today
                </button>
              </div>
              <button
                onClick={() => setCurrentDate(d => addDays(d, 1))}
                className="p-2 hover:bg-slate-200 rounded-lg transition-colors"
              >
                <ChevronRight size={20} className="text-slate-600" />
              </button>
            </div>

            {/* Add event + refresh + disconnect */}
            <div className="flex flex-col gap-2">
              <button
                onClick={openAddForm}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors"
                style={{ backgroundColor: 'var(--color-secondary)' }}
              >
                <Plus size={14} />
                Add Event
              </button>
              <button
                onClick={fetchEvents}
                disabled={loading}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-primary)' }}
              >
                <RefreshCw size={14} style={loading ? { animation: 'spin 1s linear infinite' } : {}} />
                Refresh
              </button>
              <button
                onClick={onGoogleSignOut}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors"
              >
                <LogOut size={14} />
                Disconnect
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Add Event form */}
      {showAddForm && (
        <div className="mb-6 bg-white rounded-xl border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-slate-800">New Event</h2>
            <button
              onClick={() => setShowAddForm(false)}
              className="p-1.5 hover:bg-slate-100 rounded-lg transition-colors"
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>

          <div className="space-y-4">
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleAddEvent(); }}
              placeholder="Event title"
              autoFocus
              disabled={savingEvent}
              className="w-full px-4 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none disabled:bg-slate-100"
            />

            <div className="flex flex-wrap items-center gap-4">
              <div>
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Date</label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  disabled={savingEvent}
                  className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none disabled:bg-slate-100"
                />
              </div>

              {!newAllDay && (
                <>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Start</label>
                    <input
                      type="time"
                      value={newStartTime}
                      onChange={(e) => setNewStartTime(e.target.value)}
                      disabled={savingEvent}
                      className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none disabled:bg-slate-100"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">End</label>
                    <input
                      type="time"
                      value={newEndTime}
                      onChange={(e) => setNewEndTime(e.target.value)}
                      disabled={savingEvent}
                      className="px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none disabled:bg-slate-100"
                    />
                  </div>
                </>
              )}

              <label className="flex items-center gap-2 mt-5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={newAllDay}
                  onChange={(e) => setNewAllDay(e.target.checked)}
                  disabled={savingEvent}
                  className="w-4 h-4 cursor-pointer"
                />
                <span className="text-sm text-slate-700 font-medium">All day</span>
              </label>
            </div>

            {addError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-red-700 text-sm">{addError}</p>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={handleAddEvent}
                disabled={savingEvent}
                className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white transition-colors disabled:opacity-50"
                style={{ backgroundColor: 'var(--color-secondary)' }}
              >
                {savingEvent ? <Loader size={14} className="animate-spin" /> : <Plus size={14} />}
                {savingEvent ? 'Saving...' : 'Save Event'}
              </button>
              <button
                onClick={() => setShowAddForm(false)}
                disabled={savingEvent}
                className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Connection status bar */}
      <div className="mb-4 flex items-center gap-2 px-1">
        <div className="w-2.5 h-2.5 rounded-full bg-emerald-500"></div>
        <span className="text-sm text-slate-600 font-medium">Connected to Google Calendar</span>
        {loading && <RefreshCw size={13} className="text-slate-400 ml-1" style={{ animation: 'spin 1s linear infinite' }} />}
      </div>

      {/* Error */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}

      {/* Timeline */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        <div style={{ overflowY: 'auto', maxHeight: 'calc(100vh - 300px)', minHeight: '400px' }}>
          <div style={{ position: 'relative', height: `${totalHeight}px`, marginLeft: '56px', marginRight: '16px' }}>
            {Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => {
              const hour = START_HOUR + i;
              const label = hour === 12 ? '12 PM' : hour > 12 ? `${hour - 12} PM` : `${hour} AM`;
              return (
                <div key={hour} style={{
                  position: 'absolute', top: `${i * HOUR_HEIGHT_PX}px`,
                  left: 0, right: 0, height: `${HOUR_HEIGHT_PX}px`,
                  borderTop: '1px solid #f1f5f9',
                }}>
                  <span style={{
                    position: 'absolute', left: '-52px', top: '-8px',
                    fontSize: '11px', fontWeight: 600, color: '#94a3b8',
                    width: '48px', textAlign: 'right', whiteSpace: 'nowrap',
                  }}>
                    {label}
                  </span>
                </div>
              );
            })}

            {nowPosition !== null && (
              <div ref={nowLineRef} style={{
                position: 'absolute', top: `${nowPosition}px`,
                left: 0, right: 0, height: '2px',
                backgroundColor: '#ef4444', zIndex: 10, pointerEvents: 'none',
              }}>
                <div style={{
                  position: 'absolute', left: '-6px', top: '-4px',
                  width: '10px', height: '10px',
                  borderRadius: '50%', backgroundColor: '#ef4444',
                }} />
              </div>
            )}

            {events.map((event) => {
              const { top, height } = positionEvent(event);
              const color = colorForCalendar(event.organizer?.email);
              const isExpanded = expanded === event.id;
              return (
                <div key={event.id} onClick={() => setExpanded(isExpanded ? null : event.id)} style={{
                  position: 'absolute', top: `${top}px`, left: '4px', right: 0,
                  height: isExpanded ? 'auto' : `${height}px`,
                  minHeight: `${height}px`,
                  backgroundColor: color + '18', borderLeft: `4px solid ${color}`,
                  borderRadius: '6px', padding: '5px 10px',
                  cursor: 'pointer', overflow: 'hidden', zIndex: 5,
                  transition: 'box-shadow 0.15s ease',
                  boxShadow: isExpanded ? '0 4px 12px rgba(0,0,0,0.12)' : '0 1px 3px rgba(0,0,0,0.06)',
                }}>
                  <p style={{
                    margin: 0, fontSize: '13px', fontWeight: 700,
                    color: '#1e293b', lineHeight: 1.3,
                    overflow: 'hidden', textOverflow: 'ellipsis',
                    whiteSpace: isExpanded ? 'normal' : 'nowrap',
                  }}>
                    {event.summary || '(No title)'}
                  </p>
                  <p style={{
                    margin: '2px 0 0', fontSize: '11px', color: '#64748b',
                    display: 'flex', alignItems: 'center', gap: '4px',
                  }}>
                    <Clock size={10} />
                    {formatTime(event.start.dateTime)} – {formatTime(event.end.dateTime)}
                  </p>
                  {isExpanded && event.location && (
                    <p style={{ margin: '5px 0 0', fontSize: '12px', color: '#475569' }}>
                      📍 {event.location}
                    </p>
                  )}
                  {isExpanded && event.description && (
                    <p style={{
                      margin: '5px 0 0', fontSize: '12px', color: '#475569',
                      lineHeight: 1.5, borderTop: '1px solid #e2e8f0', paddingTop: '5px',
                    }}>
                      {event.description.replace(/<[^>]*>/g, '').slice(0, 300)}
                    </p>
                  )}
                </div>
              );
            })}

            {!loading && events.length === 0 && (
              <div style={{
                position: 'absolute', top: '50%', left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center', color: '#cbd5e1',
              }}>
                <Calendar size={36} style={{ marginBottom: '10px' }} />
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 500 }}>No events for this day</p>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
